/**
 * Filling crfPackage/visitMapping (the Form Assignments grid) from the
 * protocol, without ever undoing a person's work (Jim, 2026-10-29: "the matrix
 * should be filled in from the protocol but it should be updatable by the
 * user").
 *
 * The rules, in the order they matter:
 *   1. A fill only ADDS ticks. It never removes one, whoever made it.
 *   2. A cell the person cleared (unticked a tick that was saved) is recorded
 *      in `personCleared` when they Save, and a fill NEVER adds it again.
 *   3. Every cell a fill adds is recorded in `autoTicks`, so the screen can say
 *      "filled from the protocol" and a person can tell it from their own tick.
 *   4. Running a fill twice adds nothing the second time and asks for no write.
 *   5. A form left on no visit may be proposed as "as needed" (asNeededForms.ts),
 *      recorded in `asNeeded` as "proposed". The protocol wins (a proposal is
 *      released when a fill places the form) and so does a person (a form they
 *      set as needed gets no ticks; one they cleared is never proposed again).
 *
 * Where a tick comes from: `visits[].forms` (strict matches, real form names
 * put there by visitFormMatch.ts) and `visits[].modelMatches` (the model's
 * choices, already checked against the real forms; visitMatrixFill.ts). A name
 * or id that is not one of the study's forms adds nothing.
 *
 * Used by the server (schedule/fillVisitMatrixCallable.ts, which adds the
 * transaction and docVersion) and by the CRF Builder page (the Save diff and
 * the staleness signal), so the two can never disagree.
 *
 * Pure. No Firestore, no React.
 */
import { normalizeFormName } from "../utils/scheduleFormOrder";
import { ownWindowText } from "./seedVisitMapping";
import { asNeededOf, type AsNeededBasis } from "./asNeededForms";

export interface MappingForm {
  id: string;
  name: string;
  category?: string;
}

export interface MappingConfigVisit {
  id: string;
  name?: unknown;
  window?: unknown;
  timing?: unknown;
  day?: unknown;
  forms?: unknown;
  modelMatches?: unknown;
  [key: string]: unknown;
}

type Cells = Record<string, string[]>;

export interface MergeResult {
  /** The whole mapping body to write (without editedAt and docVersion), or null when there is nothing to add. */
  mapping: Record<string, any> | null;
  /** The cells this merge added. */
  added: Array<{ visitId: string; formId: string }>;
  /** Forms this merge newly recorded as "as needed" (proposed). */
  asNeededAdded: string[];
  /** Proposed "as needed" forms released because the protocol now places them on a visit. */
  asNeededReleased: string[];
}

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

const sortedUnique = (ids: Iterable<string>): string[] => [...new Set(ids)].sort();

/** visit id -> form ids, from a saved mapping. */
export function cellsOfMapping(mapping: { visits?: unknown } | null | undefined): Cells {
  const out: Cells = {};
  for (const v of Array.isArray(mapping?.visits) ? (mapping!.visits as any[]) : []) {
    if (typeof v?.id !== "string") continue;
    out[v.id] = (Array.isArray(v.forms) ? v.forms : []).filter((x: unknown): x is string => typeof x === "string");
  }
  return out;
}

function cellMap(obj: unknown): Cells {
  const out: Cells = {};
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return out;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const ids = (Array.isArray(v) ? v : []).filter((x): x is string => typeof x === "string");
    if (ids.length) out[k] = sortedUnique(ids);
  }
  return out;
}

/** The form ids the protocol asks for on a visit: strict matches, then the model's. */
export function protocolFormIds(v: MappingConfigVisit, forms: MappingForm[]): string[] {
  const byName = new Map<string, string>();
  const byNorm = new Map<string, string>();
  const ids = new Set<string>();
  for (const f of forms) {
    ids.add(f.id);
    if (!byName.has(f.name)) byName.set(f.name, f.id);
    const k = normalizeFormName(f.name);
    if (k && !byNorm.has(k)) byNorm.set(k, f.id);
  }
  const resolveName = (name: unknown): string | undefined => {
    const t = str(name);
    return t ? (byName.get(t) ?? byNorm.get(normalizeFormName(t))) : undefined;
  };
  const out: string[] = [];
  for (const name of Array.isArray(v.forms) ? (v.forms as unknown[]) : []) {
    const id = resolveName(name);
    if (id && !out.includes(id)) out.push(id);
  }
  for (const m of Array.isArray(v.modelMatches) ? (v.modelMatches as any[]) : []) {
    const id = typeof m?.formId === "string" && ids.has(m.formId) ? m.formId : resolveName(m?.form);
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Add the protocol's ticks to the mapping. `existing` is the stored document
 * (null when there is none). The caller writes `mapping` in a transaction with
 * docVersion moved on, and adds editedAt.
 */
export function mergeVisitMapping(input: {
  existing: Record<string, any> | null | undefined;
  visits: MappingConfigVisit[];
  forms: MappingForm[];
  stamp: { editedBy: string };
  /**
   * visit id -> form ids placed by the standard rule (standardVisitForms.ts)
   * for forms the protocol never schedules. Added like the protocol's ticks,
   * and also recorded in `standardTicks` so the page can say why.
   */
  standard?: Record<string, string[]>;
  /**
   * form id -> why it looks like an event form (asNeededForms.ts: the rule, or
   * the model's checked answer). Only a PROPOSAL: a form is recorded as needed
   * only if, after this merge, nothing has placed it on a visit and no person
   * has decided about it (ticked it, unticked it, or cleared its as-needed).
   */
  asNeededCandidates?: Record<string, AsNeededBasis>;
}): MergeResult {
  const { existing, visits, forms } = input;
  const cleared = cellMap(existing?.personCleared);
  const asNeededNow = asNeededOf(existing);
  // A form a PERSON set as needed is on no visit by their decision: a fill adds it no tick.
  const personAsNeeded = new Set(Object.entries(asNeededNow.asNeeded).filter(([, s]) => s === "person").map(([id]) => id));
  const formIds = new Set(forms.map((f) => f.id));
  const fromRule = new Set<string>();
  const formOrder = new Map(forms.map((f, i) => [f.id, i]));
  const added: Array<{ visitId: string; formId: string }> = [];

  const entries: any[] = Array.isArray(existing?.visits) ? (existing!.visits as any[]).map((v) => ({ ...v })) : [];
  const entryById = new Map<string, any>(entries.filter((e) => typeof e?.id === "string").map((e) => [e.id, e]));

  for (const v of visits ?? []) {
    if (typeof v?.id !== "string") continue;
    const protocol = protocolFormIds(v, forms);
    const rule = (input.standard?.[v.id] ?? []).filter((id) => formIds.has(id) && !protocol.includes(id));
    for (const id of rule) fromRule.add(`${v.id}|${id}`);
    const want = [...protocol, ...rule].filter((id) => !(cleared[v.id] ?? []).includes(id) && !personAsNeeded.has(id));
    let entry = entryById.get(v.id);
    const have = new Set<string>(Array.isArray(entry?.forms) ? entry.forms : []);
    const fresh = want.filter((id) => !have.has(id)).sort((a, b) => (formOrder.get(a) ?? 0) - (formOrder.get(b) ?? 0));
    if (fresh.length === 0) continue;
    if (!entry) {
      const w = ownWindowText(v);
      entry = { id: v.id, name: v.name as string, ...(w !== undefined ? { window: w } : {}), forms: [] as string[] };
      entries.push(entry);
      entryById.set(v.id, entry);
    }
    entry.forms = [...(Array.isArray(entry.forms) ? entry.forms : []), ...fresh];
    for (const formId of fresh) added.push({ visitId: v.id, formId });
  }

  // As needed. THE PROTOCOL WINS: a proposal is released when a fill places the
  // form on a visit, and a form is proposed only if nothing has placed it.
  const placedNow = new Set<string>();
  for (const e of entries) for (const id of Array.isArray(e?.forms) ? e.forms : []) placedNow.add(id);
  const decidedByPerson = new Set<string>(Object.values(cleared).flat());
  const released = Object.entries(asNeededNow.asNeeded)
    .filter(([id, source]) => source === "proposed" && placedNow.has(id))
    .map(([id]) => id);
  const asNeededAdded: string[] = [];
  for (const [id, basis] of Object.entries(input.asNeededCandidates ?? {})) {
    if (!formIds.has(id) || id in asNeededNow.asNeeded || asNeededNow.asNeededCleared.includes(id)) continue;
    if (placedNow.has(id) || decidedByPerson.has(id)) continue;
    asNeededNow.asNeededBasis[id] = basis;
    asNeededAdded.push(id);
  }
  asNeededAdded.sort();

  if (added.length === 0 && asNeededAdded.length === 0 && released.length === 0) {
    return { mapping: null, added, asNeededAdded, asNeededReleased: released };
  }
  const auto = cellMap(existing?.autoTicks);
  for (const a of added) auto[a.visitId] = sortedUnique([...(auto[a.visitId] ?? []), a.formId]);
  const standardTicks = cellMap(existing?.standardTicks);
  for (const a of added) {
    if (fromRule.has(`${a.visitId}|${a.formId}`)) {
      standardTicks[a.visitId] = sortedUnique([...(standardTicks[a.visitId] ?? []), a.formId]);
    }
  }

  const inventory: any[] = Array.isArray(existing?.form_inventory) ? [...(existing!.form_inventory as any[])] : [];
  const inInventory = new Set(inventory.map((f) => f?.id));
  for (const f of forms) {
    if (inInventory.has(f.id)) continue;
    inventory.push({ id: f.id, name: f.name, layout: "question-list", category: f.category ?? "" });
  }

  // A grid made only to hold a proposal still lists every visit, as a seeded one does.
  // Verification B2 reads a grid with no ticks at all as "nobody has assigned a form
  // yet" (skipped), so this grid does not make it warn about every empty visit.
  if (!existing && entries.length === 0) {
    for (const v of visits ?? []) {
      if (typeof v?.id !== "string") continue;
      const w = ownWindowText(v);
      entries.push({ id: v.id, name: v.name as string, ...(w !== undefined ? { window: w } : {}), forms: [] as string[] });
    }
  }

  // Everything the document already carries stays (study, generatedAt, the
  // analysis timestamp, editedBy, the person's cleared cells).
  const { docVersion: _v, editedAt: _a, ...kept } = (existing ?? {}) as Record<string, any>;
  const mapping: Record<string, any> = {
    ...kept,
    visits: entries,
    form_inventory: inventory,
    autoTicks: auto,
    filledBy: "protocol",
  };
  if (Object.keys(cleared).length) mapping.personCleared = cleared;
  if (Object.keys(standardTicks).length) mapping.standardTicks = standardTicks;

  const nextAsNeeded: Record<string, string> = { ...asNeededNow.asNeeded };
  const nextBasis: Record<string, string> = { ...asNeededNow.asNeededBasis };
  for (const id of released) {
    delete nextAsNeeded[id];
    delete nextBasis[id];
  }
  for (const id of asNeededAdded) nextAsNeeded[id] = "proposed";
  if (Object.keys(nextAsNeeded).length) {
    mapping.asNeeded = nextAsNeeded;
    mapping.asNeededBasis = nextBasis;
  } else {
    delete mapping.asNeeded;
    delete mapping.asNeededBasis;
  }
  if (asNeededNow.asNeededCleared.length) mapping.asNeededCleared = asNeededNow.asNeededCleared;
  if (!existing) {
    mapping.editedBy = input.stamp.editedBy;
    mapping.seededBy = "protocol";
  }
  return { mapping, added, asNeededAdded, asNeededReleased: released };
}

/**
 * What to store as `autoTicks` and `personCleared` when a person Saves.
 *
 * `previous` is the last SAVED mapping, `nextTicks` the grid being saved. A
 * saved tick the person removed goes to `personCleared` WHATEVER its origin: an
 * old hand-made mapping can hold ticks the protocol also asks for, and a later
 * fill must not bring those back either. A cell the person ticks again leaves
 * `personCleared`. `autoTicks` keeps only cells that are still ticked, so a
 * tick the person re-adds is theirs, not "filled from the protocol".
 */
export function reconcileOnSave(
  previous: { visits?: unknown; autoTicks?: unknown; personCleared?: unknown; standardTicks?: unknown } | null | undefined,
  nextTicks: Cells,
): { autoTicks: Cells; personCleared: Cells; standardTicks: Cells } {
  const prevCells = cellsOfMapping(previous);
  const ticked = (visitId: string, formId: string) => (nextTicks[visitId] ?? []).includes(formId);

  const cleared = cellMap(previous?.personCleared);
  for (const [visitId, formIds] of Object.entries(prevCells)) {
    for (const formId of formIds) {
      if (!ticked(visitId, formId)) cleared[visitId] = sortedUnique([...(cleared[visitId] ?? []), formId]);
    }
  }
  const personCleared: Cells = {};
  for (const [visitId, formIds] of Object.entries(cleared)) {
    const left = formIds.filter((f) => !ticked(visitId, f));
    if (left.length) personCleared[visitId] = sortedUnique(left);
  }

  const stillTicked = (cells: Cells): Cells => {
    const out: Cells = {};
    for (const [visitId, formIds] of Object.entries(cells)) {
      const left = formIds.filter((f) => ticked(visitId, f));
      if (left.length) out[visitId] = left;
    }
    return out;
  };
  return {
    autoTicks: stillTicked(cellMap(previous?.autoTicks)),
    personCleared,
    standardTicks: stillTicked(cellMap(previous?.standardTicks)),
  };
}

/** A short stable fingerprint of text (FNV-1a, 32 bit, hex). Not a security hash. */
function fingerprint(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/**
 * The version of the fill's rules. Raising it makes every study's recorded fill
 * stale, so each grid is filled again (adding ticks only) the next time an
 * editor opens it. 2 (2026-10-29): several forms per procedure, and the
 * standard rule for forms the protocol never schedules. 3 (2026-10-29): a
 * refill asks again about procedures an earlier fill placed, so they can take a
 * further form. 4 (2026-11-02): forms an event drives are proposed as "as
 * needed" (asNeededForms.ts), so every study's grid is looked at once more.
 */
export const FILL_RULES_VERSION = 4;

/**
 * What the last fill was made from: the fill's rules, the latest protocol
 * analysis, the latest imported Schedule of Events and the set of CRF form
 * names. When any of them changes the fill is stale and runs again. Form names
 * are compared ignoring case, punctuation and order.
 */
export function fillSignature(input: {
  analysisAtMs: number | null | undefined;
  importedAtMs: number | null | undefined;
  formNames: string[];
}): string {
  const names = [...new Set((input.formNames ?? []).map((n) => normalizeFormName(n)).filter(Boolean))].sort();
  return `r${FILL_RULES_VERSION}|a${input.analysisAtMs ?? 0}|i${input.importedAtMs ?? 0}|f${names.length}:${fingerprint(names.join("\n"))}`;
}

/** True when no fill has been recorded, or the recorded one was made from something else. */
export function isFillStale(matrixFill: { signature?: unknown } | null | undefined, signature: string): boolean {
  return !matrixFill || typeof matrixFill.signature !== "string" || matrixFill.signature !== signature;
}
